import { season } from "../core/index.ts";
import { idb } from "../db/index.ts";
import { g } from "../util/index.ts";
import { defineView, keepType } from "../util/defineView.ts";
import { getTopPlayers, getUpcoming } from "./schedule.ts";
import { PHASE } from "../../common/constants.ts";
import { makeResponsiveDropdownOption } from "../../common/makeResponsiveDropdownOption.tsx";
import { env } from "../util/env.ts";
import type { RouteParams } from "../../ui/router/types.ts";
import { validateSeason } from "../util/processInputs.ts";

const processInputs = (params: RouteParams<"dailySchedule">) => {
	let cid;
	if (params.cid !== undefined && params.cid !== "all") {
		cid = Number.parseInt(params.cid);
		if (Number.isNaN(cid)) {
			cid = undefined;
		}
	}

	if (params.season === "today") {
		return {
			cid,
			day: undefined,
			season: g.get("season"),
			today: true,
		};
	}

	const season = validateSeason(params.season);

	let day =
		params.day === undefined ? undefined : Number.parseInt(params.day as any);
	if (Number.isNaN(day)) {
		day = 1;
	}

	return {
		cid,
		day,
		season,
	};
};

// The day actually shown, which can be different than inputs.day, such as when there is no day in the URL
const keepPrevOutput = {
	day: keepType<number>(),
};

export default defineView({
	id: "dailySchedule",
	processInputs,
	keepPrevOutput,
	load: async ({ inputs, updateEvents, prevInputs, prevOutput }) => {
		const currentSeason = g.get("season");

		if (
			updateEvents.has("firstRun") ||
			(inputs.season === currentSeason && updateEvents.has("gameSim")) ||
			updateEvents.has("newPhase") ||
			inputs.season !== prevInputs?.season ||
			inputs.day !== prevOutput.day ||
			inputs.cid !== prevInputs?.cid
		) {
			const process = async (inputsDayOverride?: number) => {
				const games = await idb.getCopies.games(
					{
						season: inputs.season,
					},
					"noCopyCache",
				);

				const daysAndPlayoffs = new Map<number, boolean>();
				for (const game of games) {
					if (game.day !== undefined) {
						daysAndPlayoffs.set(game.day, !!game.playoffs);
					}
				}

				let isToday = false;

				let day: number;
				if (inputs.today) {
					day = -1;
				} else {
					// What day is it? Get it from URL by default, but that could be undefined
					day = inputsDayOverride ?? inputs.day ?? -1;
					if (day === -1) {
						if (updateEvents.has("firstRun")) {
							// If this is a new load of the view, initialize to the current day (current season) or day 1 (past season)
							day = -1;
						} else if (prevInputs?.day !== undefined) {
							// If this is a refresh and we're moving from day in URL to no day in URL, go to current day (current season) or day 1 (past season)
							day = -1;
						} else if (prevOutput.day !== undefined) {
							// If this is a refresh and we already had a day loaded even with no day in the URL, keep that day the same
							day = prevOutput.day;
						}
					}
				}

				if (inputs.season === currentSeason) {
					const schedule = await season.getSchedule();

					if (day === -1) {
						if (schedule[0]?.day !== undefined) {
							day = schedule[0].day;
						}
					}
					if (day === -1) {
						day = 1;
					}

					const scheduleDay = schedule.filter((game) => game.day === day);
					isToday = !!scheduleDay[0] && schedule[0]!.gid === scheduleDay[0].gid;

					const isPlayoffs = g.get("phase") === PHASE.PLAYOFFS;

					for (const game of schedule) {
						if (game.day !== undefined) {
							daysAndPlayoffs.set(game.day, isPlayoffs);
						}
					}
				} else {
					if (day === -1) {
						day = 1;
					}
				}

				// Allowing the games to be filtered by conference is really only particularly useful in large leagues, and on mobile it makes the title bar take up an extra row. So only show it for large league or non-mobile.
				const showCids = g.get("numActiveTeams") >= 60 || !env.mobile;

				const confs = g.get("confs", inputs.season);
				let cid: number | undefined;
				if (showCids && inputs.cid !== undefined) {
					cid = confs.find((conf) => conf.cid === inputs.cid)?.cid;
				}

				let cidFilter:
					| ((teams: [{ tid: number }, { tid: number }]) => boolean)
					| undefined;
				if (cid !== undefined) {
					const tids = new Set(
						(await idb.cache.teams.getAll())
							.filter((t) => t.cid === cid)
							.map((t) => t.tid),
					);

					cidFilter = (teams) => {
						const homeTid = teams[0].tid;
						const awayTid = teams[1].tid;
						return (
							(homeTid === -1 && awayTid === -2) ||
							(homeTid === -3 && awayTid === -3) ||
							tids.has(awayTid) ||
							tids.has(homeTid)
						);
					};
				}
				const completed = games.filter(
					(game) => game.day === day && (!cidFilter || cidFilter(game.teams)),
				);

				let upcoming: Awaited<ReturnType<typeof getUpcoming>> = [];
				if (inputs.season === currentSeason) {
					// If it's the current season, get any upcoming games
					upcoming = await getUpcoming({
						cid,
						day,
					});
				}

				const cids = showCids
					? [
							{
								key: "all",
								value: makeResponsiveDropdownOption(
									"All confs",
									"All conferences",
								),
							},
							...confs.map((conf) => {
								// Shorten "Eastern Conference" to "Eastern Conf" on mobile
								const value = conf.name.endsWith("onference")
									? makeResponsiveDropdownOption(
											conf.name.slice(0, -"erence".length),
											conf.name,
										)
									: conf.name;

								return {
									key: conf.cid,
									value,
								};
							}),
						]
					: [];

				const days = Array.from(daysAndPlayoffs.entries())
					.map(([day, playoffs]) => ({ day, playoffs }))
					.sort((a, b) => a.day - b.day)
					.map(({ day, playoffs }) => ({
						key: day,
						value: playoffs ? `${day} (playoffs)` : `${day}`,
					}));

				if (inputs.season !== currentSeason) {
					// Add team branding info, in case that was different in past season. Otherwise, ScoreBox uses teamInfoCache for latest values
					for (const [i, game] of completed.entries()) {
						completed[i] = { ...game };

						for (const t of game.teams) {
							const teamSeason = await idb.getCopy.teamSeasons({
								season: game.season,
								tid: t.tid,
							});

							if (teamSeason) {
								t.branding = {
									region: teamSeason.region,
									name: teamSeason.name,
									abbrev: teamSeason.abbrev,
									imgURL: teamSeason.imgURL,
									imgURLSmall: teamSeason.imgURLSmall,
								};
							}
						}
					}
				}

				return {
					cid,
					cids,
					completed,
					day,
					days,
					isToday,
					upcoming,
				};
			};

			let info = await process();

			if (
				info.completed.length === 0 &&
				info.upcoming.length === 0 &&
				info.days.length > 0
			) {
				const dayAbove = info.days.find(({ key }) => key > info.day);

				const newDay = dayAbove ? dayAbove.key : info.days.at(-1)!.key;

				// No games at requested day, so just use the last day we actually have games for
				info = await process(newDay);
			}

			const topPlayers = await getTopPlayers(undefined, 1, info.day);

			return {
				...info,
				elam: g.get("elam"),
				elamASG: g.get("elamASG"),
				season: inputs.season,
				ties: season.hasTies("current"),
				topPlayers,
			};
		}
	},
});
